/**
 * 购物车邮件询价（EMAIL QUOTE）。主题 assets/mziho-email-quote.js，源文件 src/theme-assets/mziho-email-quote.js。
 *
 * 提交时把整个购物车序列化进隐藏字段：店主收到的邮件里带每件商品的变体、数量、
 * 全部配置选项和小计，不用再回后台翻购物车。下划线开头的属性是程序自用的，不进邮件。
 *
 * 2026-10-22 改成不刷新提交（店主选方案 B + A 兜底）：
 *   Shopify 的 hCaptcha 脚本在「绑定」（客户第一次 focus 表单）时把 form.submit 存起来，拿到令牌后调用它。
 *   这里在绑定前把 form.submit 换成 send()：填购物车明细 → fetch POST /contact（令牌照常带上）→
 *     跳转后 URL 含 contact_posted=true → 原地显示成功卡片 +「Send another request」；
 *     含 /challenge（要人工验证）→ 打开 Shopify 验证页，验证后它补发并跳回；其它 → 报错，按钮恢复可重试。
 *   整页提交回来时（?contact_posted=true）也补一张成功卡片。原来靠 Liquid 的 request.url 判断，
 *   Shopify 没有这个变量，提示从来没出现过。
 *   购物车抽屉会整段重渲染，所以用 MutationObserver 给新出现的表单也接上。
 * JS 失败时表单照常原生提交，只是少了明细和原地提示 —— 不挡客户。
 */
(() => {
  const SELECTOR = 'form.mziho-email-quote__form';
  // 整页提交回来的兜底提示只在购物车页补；抽屉里发的回到商品页时，由商品页配置器的提示接手
  const posted = /[?&]contact_posted=true/.test(location.search) && /\/cart\/?$/.test(location.pathname);

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return `${(cents / 100).toFixed(2)}`;
    }
  };

  // 2026-10-22：加价行（$0.01 的 product-options，或带 _Configurator parent 的付费附加品）不单列 ——
  //   原来邮件里会出现「Product options 6280 × $0.01」。并到对应商品下面写一行「Options upgrade: +$62.80」，总价不变。
  const isChild = (item) => item.handle === 'product-options' || Boolean(item.properties && item.properties['_Configurator parent']);

  const buildSummary = (cart) => {
    const upgrades = new Map();   // _Configuration ID -> 加价合计（分）
    let unmatched = 0;            // 找不到主商品的加价行（理论上会被购物车清理掉），单列一行保证总价对得上
    const mains = cart.items.filter((item) => !isChild(item));
    const ids = new Set(mains.map((item) => item.properties && item.properties['_Configuration ID']).filter(Boolean));
    cart.items.filter(isChild).forEach((item) => {
      const parent = item.properties && item.properties['_Configurator parent'];
      if (parent && ids.has(parent)) upgrades.set(parent, (upgrades.get(parent) || 0) + item.final_line_price);
      else unmatched += item.final_line_price;
    });
    const lines = mains.map((item, i) => {
      const opts = Object.entries(item.properties || {})
        .filter(([k, v]) => !k.startsWith('_') && k && v !== '' && v != null)
        .map(([k, v]) => `${k}: ${v}`);
      const upgrade = upgrades.get(item.properties && item.properties['_Configuration ID']) || 0;
      return [
        `${i + 1}) ${item.product_title}${item.variant_title ? ` — ${item.variant_title}` : ''}`,
        opts.length ? `   ${opts.join(' · ')}` : null,
        `   ${item.quantity} × ${money(item.final_price)} = ${money(item.final_line_price)}`,
        upgrade ? `   Options upgrade: +${money(upgrade)}` : null,
      ].filter(Boolean).join('\n');
    });
    if (unmatched) lines.push(`Other options: +${money(unmatched)}`);
    lines.push('', `Cart total: ${money(cart.total_price)}`);
    return lines.join('\n');
  };

  const fillCart = async (form) => {
    const target = form.querySelector('[data-mziho-quote-cart]');
    if (!target) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cart = await fetch(`${root.endsWith('/') ? root : root + '/'}cart.js`).then((r) => r.json());
      target.value = buildSummary(cart);
    } catch (e) {
      target.value = '(cart details unavailable)';
    }
  };

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  // 成功卡片放在表单前面；表单只是藏起来，「Send another request」把它原样放回来（填过的内容都在）
  const showSent = (form, email) => {
    const box = document.createElement('div');
    box.className = 'mziho-email-quote__success';
    box.setAttribute('role', 'status');
    box.innerHTML = '<strong>✓ Quote request sent</strong>' +
      `<span>We'll email your quote${email ? ` to <b>${esc(email)}</b>` : ''} within one business day.</span>` +
      '<button type="button" class="mziho-email-quote__again">Send another request</button>';
    box.querySelector('button').addEventListener('click', () => {
      box.remove();
      form.hidden = false;
      form.querySelector('input[name="contact[name]"]')?.focus();
    });
    form.hidden = true;
    form.before(box);
    box.closest('details')?.setAttribute('open', '');
    box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const showError = (form, message) => {
    let box = form.querySelector('.mziho-email-quote__error');
    if (!box) {
      box = document.createElement('p');
      box.className = 'mziho-email-quote__error';
      box.setAttribute('role', 'alert');
      form.querySelector('.mziho-email-quote__submit')?.before(box);
    }
    box.textContent = message;
    box.hidden = !message;
  };

  const setBusy = (form, busy) => {
    const button = form.querySelector('.mziho-email-quote__submit');
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
    const text = button.querySelector('.button-text-main') || button;
    if (!button.dataset.label) button.dataset.label = text.textContent;
    text.textContent = busy ? 'Sending…' : button.dataset.label;
  };

  const send = async (form) => {
    setBusy(form, true);
    showError(form, '');
    await fillCart(form);
    let response = null;
    try {
      response = await fetch(new URL(form.action, location.href).pathname, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
    } catch (e) { /* 网络错误，走下面的报错 */ }
    const url = (response && response.url) || '';
    if (response && response.ok && url.includes('contact_posted=true')) {
      setBusy(form, false);
      showSent(form, form.querySelector('input[name="contact[email]"]')?.value.trim() || '');
      return;
    }
    // 要人工验证：打开 Shopify 给的验证页（表单内容已被验证码脚本按 form_key 存进 sessionStorage，
    // 验证后由 Shopify 补发并跳回购物车）。不能拿用过的令牌原生重发，会 400 Invalid CAPTCHA token。
    if (url.includes('/challenge')) {
      location.assign(url);
      return;
    }
    setBusy(form, false);
    showError(form, 'We could not send your request just now. Please try again, or email us directly and we will quote you.');
  };

  const wire = (form) => {
    if (form.dataset.mzihoQuoteWired) return;
    form.dataset.mzihoQuoteWired = '1';
    // 必须赶在验证码绑定之前：绑定时它会把这时的 form.submit 存起来，拿到令牌后调用
    form.submit = () => send(form);
    form.addEventListener('submit', (event) => {
      // 验证码已绑定：它自己的 submit 监听会 preventDefault → 求解 → 调上面的 send()，这里不插手
      if (form.dataset.hcaptchaBound === 'true' || form.dataset.recaptchaBound === 'true') return;
      // 没绑上（店铺关了验证码，或脚本没加载）：自己发，没有令牌被挡时 send() 会退回整页提交
      event.preventDefault();
      send(form);
    });
    if (posted && !form.dataset.mzihoPostedShown) {
      form.dataset.mzihoPostedShown = '1';
      showSent(form, '');
      const clean = new URL(location.href);
      clean.searchParams.delete('contact_posted');
      clean.hash = '';
      history.replaceState(history.state, '', clean);
    }
  };

  document.querySelectorAll(SELECTOR).forEach(wire);
  new MutationObserver(() => document.querySelectorAll(SELECTOR).forEach(wire))
    .observe(document.body, { childList: true, subtree: true });
})();
